import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { auth, db } from './firebaseConfig';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { ThemeProvider } from './components/ThemeContext';
import Navbar from './components/NavBar';
import Login from './components/Login';
import Signup from './components/SignUp';
import Profile from './components/Profile';
import Feed from './components/Feed';
import AdminDashboard from './components/AdminDashboard';
import CaughtDreams from './components/CaughtDreams';
import Network from './components/Network';
import Settings from './components/Settings';
import PostPage from './components/PostPage';
import LandingPage from './components/LandingPage';
import HelpSupport from './components/HelpSupport';
import SingleDreamPage from './components/SingleDreamPage';
import { AuthProvider } from './contexts/AuthContext';
import { initHotjar } from './utils/hotjar';
import Footer from './components/Footer';
import { AnalyticsProvider } from './contexts/AnalyticsContext';
import { NotificationProvider } from './contexts/NotificationContext';
import Notifications from './components/Notifications';
import ProtectedRoute from './components/ProtectedRoute';

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userProfile, setUserProfile] = useState(null);
  const [darkMode, setDarkMode] = useState(true);

  useEffect(() => {
    // Initialize Hotjar
    initHotjar();

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          const userDoc = await getDoc(doc(db, 'profiles', user.uid));
          if (userDoc.exists()) {
            const profileData = userDoc.data();
            setUserProfile(profileData);
            setUser({
              ...user,
              username: profileData.username,
              role: profileData.role
            });
          }
          setIsAuthenticated(true);
        } catch (error) {
          console.error('Error fetching user profile:', error);
          setIsAuthenticated(false);
          setUser(null);
          setUserProfile(null);
        }
      } else {
        setIsAuthenticated(false);
        setUser(null);
        setUserProfile(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
      document.body.style.backgroundImage = 'none';
      document.body.style.backgroundColor = '#1a1a1a';
    } else {
      document.documentElement.classList.remove('dark');
      document.body.style.backgroundImage = 'url("/bg.jpg")';
      document.body.style.backgroundSize = 'cover';
      document.body.style.backgroundAttachment = 'fixed';
      document.body.style.backgroundPosition = 'center';
      document.body.style.backgroundRepeat = 'no-repeat';
    }
  }, [darkMode]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  return (
    <AuthProvider>
      <NotificationProvider>
      <Router>
        <div className="min-h-screen">
          <ThemeProvider>
            <div className="min-h-screen">
              <Navbar 
                isAuthenticated={isAuthenticated} 
                setIsAuthenticated={setIsAuthenticated}
                darkMode={darkMode}
                setDarkMode={setDarkMode}
                user={user}
                userProfile={userProfile}
              />
              <div className="pt-8  max-w-11xl mx-auto">
              <AnalyticsProvider>
                <Routes>
                  <Route 
                    path="/" 
                    element={
                      isAuthenticated ? (
                        <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-black text-white">
                          <Feed />
                        </div>
                      ) : (
                        <LandingPage />
                      )
                    } 
                  />
                  <Route 
                    path="/login" 
                    element={!isAuthenticated ? <Login setIsAuthenticated={setIsAuthenticated} /> : <Navigate to="/" />} 
                  />
                  <Route 
                    path="/signup" 
                    element={!isAuthenticated ? <Signup /> : <Navigate to="/" />} 
                  />
                  <Route 
                    path="/dreamer/:username" 
                    element={
                      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-black text-white">
                        <Profile />
                      </div>
                    } 
                  />
                  <Route 
                    path="/network" 
                    element={
                      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-black text-white">
                        <Network />
                      </div>
                    } 
                  />
                  <Route 
                    path="/caught-dreams" 
                    element={
                      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-black text-white">
                        <CaughtDreams />
                      </div>
                    } 
                  />
                  <Route 
                    path="/post/:postId" 
                    element={
                      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-black text-white">
                        <SingleDreamPage />
                      </div>
                    } 
                  />
                  <Route 
                    path="/admin" 
                    element={
                      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-black text-white">
                        <AdminDashboard />
                      </div>
                    } 
                  />
                  <Route 
                    path="/help" 
                    element={
                      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-black text-white">
                        <HelpSupport />
                      </div>
                    } 
                  />
                   <Route 
                    path="/settings" 
                    element={
                      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-black text-white">
                        <Settings />
                      </div>
                    } 
                  />
                  <Route path="/dream/:dreamId" element={<SingleDreamPage />} />
                  <Route 
                    path="/notifications" 
                    element={
                      <ProtectedRoute>
                        <Notifications />
                      </ProtectedRoute>
                    } 
                  />
                </Routes>
                </AnalyticsProvider>
              </div>
              <Footer /> 
            </div>
          </ThemeProvider>
        </div>
      </Router>
      </NotificationProvider>
    </AuthProvider>
  );
}

export default App;
